// src/components/ShaTelephony/models.ts
import { NormalizedPhone } from "../providers/utils/phoneUtils";

export enum CallStatus {
  Idle = "IDLE",
  Ringing = "RINGING",
  InCall = "IN_CALL",
  Transferring = "TRANSFERRING",
  Awaiting = "AWAITING",
}

export enum CallDirection {
  Inbound = "INBOUND",
  Outbound = "OUTBOUND",
}

export enum CallCategorization {
  General = 1,
  FollowUp = 2,
  CreateCase = 3,
}

export interface CallDetails {
  id?: string;
  callerNumber?: string;
  callerName?: string;
  direction: CallDirection;
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  recordingUrl?: string;
  recordingId?: string;
  numberDetails?: NormalizedPhone;
  agentId?: string;
  customerId?: string;
  callCategorization?: CallCategorization;
  callLogSaved?: boolean;
}

export interface TelephonyEvent {
  eventType: number;
  phoneNumber?: string;
  agentId?: string;
  agentState?: number;
  recordingId?: string;
  fullEventJsonInfo?: string;
}

export enum TelephonyEventType {
  AgentStateChanged = 1,
  IncomingCallRinging = 2,
  CallAnswered = 3,
  OutboundCallInitiated = 4,
  CallConnected = 5,
  CallEnded = 6,
  CallTransferred = 7,
  CallRejected = 8,
  SessionEnded = 9,
  Unkown = 10,
  LineStateChanged = 11,
  CallRecorded = 12,
}

export interface LineStatus {
  isFree?: boolean;
  isActiveCall?: boolean;
}

export interface TelephonyProviderState {
  isLoggedIn: boolean;
  isAvailable: boolean;
  status: CallStatus;
  sessionId?: string;
  callerNumber?: string;
  callerName?: string;
  currentCallId?: string;
  currentCallDetails?: CallDetails;
  currentCallDirection?: CallDirection;
  recordingId?: string;
}

export interface ITelePhonyAgent {
  username: string;
  password: string;
  hasInbound?: boolean;
  hasOutbound?: boolean;
}

export interface ITelephonyProvider {
  supportsPopout: boolean;
  supportsCustomViews: boolean;
  enableRejectCall?: boolean;
  config: any;
  sessionId: string;
  setCurrentCall?: (call: any) => void;
  setCallerName?: (name: string) => void;
  muteCall?(): Promise<void>;
  unmuteCall?(): Promise<void>;
  transferCaller?(): Promise<void>;
  holdCall?(): Promise<void>;
  unholdCall?(callId?: number): Promise<void>;
  // Authentication methods
  shouldShowLoginDialog(): boolean;

  getExistingLogin(): Promise<any>;
  agentLogin(): any;
  login(): Promise<boolean>;
  logout(): Promise<boolean>;
  setLoginCredentials(
    username: string,
    password: string,
    station: string
  ): void;
  setSession(sessionId: string): void;
  release(): void;

  // Availability methods
  setOnBreak(): void;
  setAvailable(): Promise<void>;
  setOnBreakWithReason(reasonCode: number): void;

  // Shift management (optional - only implemented by providers that support it)
  endShift?(reasonCode: number): Promise<boolean>;

  // Call management methods
  dial(number: string, originalNumber?: string): void;
  answerCall(): void;
  rejectCall(): void;
  endCall(): void;
  finaliseCall(): Promise<void>;
  transferCall(phoneNumber: string, isAgent: boolean): Promise<void>;

  // Info methods
  getCallDetails(callId?: string): CallDetails | null;
  //getRecordingUrl(callId: string): Promise<string | null>;

  // State
  getState(): TelephonyProviderState;

  // Event registration
  onIncomingCallRinging(callback: (details: CallDetails) => void): void;
  onIncomingCallAnswered(callback: (details: CallDetails) => void): void;
  onOutboundCallInitiated(callback: (details: CallDetails) => void): void;
  onOutboundCallAnswered(callback: (details: CallDetails) => void): void;
  onCallTransferred(callback: (details: CallDetails) => void): void;
  onCallEnded(callback: (details: CallDetails) => void): void;
  onIncomingCallRejected(callback: (details: CallDetails) => void): void;
  onOutgoingCallRejected(callback: (details: CallDetails) => void): void;
  onAvailabilityChanged(callback: (isAvailable: boolean) => void): void;
  onLoggedInChanged(callback: (isLoggedIn: boolean) => void): void;
  onStatusChanged(callback: (status: CallStatus) => void): void;
}

export interface InnovaphoneConfig {
  apiBaseUrl: string;
  hubUrl: string;
  // Innovaphone WebAgent connection settings
  serverUrl: string; // e.g., https://10.254.254.183/webagent/

  // Agent credentials
  username: string; // e.g., 1990
  password: string; // e.g., 1234

  // Agent session
  sessionId: string;

  // Campaign configuration
  campaign?: string; // e.g., 890
  skill?: string; // e.g., 8890
  vdn?: string; // e.g., 7990
  extension?: string; // e.g., 6990
  defaultCountryCode?: string;

  // Advanced settings
  customFields?: {
    [key: string]: string;
  };
}

export interface SignalRTelephonyConfig {
  // SignalR Hub URL
  hubUrl: string; // e.g., https://localhost:44362/telephonyHub

  // API Base URL for REST calls
  apiBaseUrl: string; // e.g., https://localhost:44362

  // Agent configuration
  agentId?: string;
  station?: string;

  // Additional settings
  autoReconnect?: boolean;
  reconnectInterval?: number;
}

export interface AwsConnectConfig {
  // AWS Connect Instance settings
  instanceId: string; // e.g., "12345678-1234-1234-1234-123456789012"
  instanceUrl: string; // e.g., "https://mycompany.awsapps.com/connect"
  region: string; // e.g., "us-east-1"

  // SignalR and API settings
  hubUrl: string; // e.g., "https://localhost:44362/telephonyHub"
  apiBaseUrl: string; // e.g., "https://localhost:44362"

  // Additional AWS Connect settings
  routingProfileId?: string;
  queueIds?: string[];
  defaultCountryCode?: string;
  contactFlowId?: string;
}

export interface TelephonyContextType {
  getTelephonyAgent: () => Promise<ITelePhonyAgent | null>;
  provider: ITelephonyProvider | null;
  providerState: TelephonyProviderState;
  initialize: (providerType: string, config: any) => Promise<boolean>;
  /** True once the SignalR connection is established and safe to pass to a provider. */
  isConnectionReady: boolean;
}

export interface TelephonyProfile {
  id: string;
  personId: string;
  username: string;
  password: string; // This would be encrypted in a real implementation
  providerType: string;
  providerConfig: any;
}

export interface IHttpHeadersDictionary {
  [key: string]: string;
}
